// peaks-fallback (SP2) — when the publish pipeline did not emit per-track peaks
// (manifest.perTrackPeaksUrl absent, e.g. v1 shares / dev fixture), generate
// them on the client by decoding each track's audio and downsampling. Mirrors
// desktop backing-player.getPeaksFromAudioUuid (decode → downsamplePeaks).
//
// decodeAudio is injected so the hot path (real OfflineAudioContext /
// AudioContext.decodeAudioData) stays testable under happy-dom. binCount is
// chosen by the caller (typically the overview SummaryPeaks.binCount, so all
// rows share a bin resolution).

import type { ShareManifest, PerTrackSummaryPeaks } from '@bandway/share-types';
import { downsamplePeaks } from '@bandway/ui-primitives';

export interface FallbackDeps {
  /** Decode a track url to an AudioBuffer. Throws on failure. */
  decodeAudio: (url: string) => Promise<AudioBuffer>;
  /** Target bins per track. */
  binCount: number;
  /** Decodes in flight at once (default 2). Each decoded AudioBuffer is the
   *  whole stem as float32 — all of them at once was ~1 GB on a 16-stem song
   *  and crashed the iPhone Safari tab (B-260927-share-web-peaks-fallback-contexts). */
  concurrency?: number;
}

const DEFAULT_CONCURRENCY = 2;

function hasBins(p: PerTrackSummaryPeaks | null, id: string): boolean {
  return !!p && Array.isArray(p.tracks[id]) && p.tracks[id].length > 0;
}

export async function computePerTrackPeaksFallback(
  manifest: ShareManifest,
  existing: PerTrackSummaryPeaks | null,
  deps: FallbackDeps,
): Promise<PerTrackSummaryPeaks> {
  const tracks: Record<string, number[]> = { ...(existing?.tracks ?? {}) };
  const todo = manifest.tracks.filter((t) => !hasBins(existing, t.id)); // keep the ones that have bins

  // A small pool instead of Promise.all over every stem: at most `concurrency`
  // decoded buffers are alive at once, and each is dropped as soon as its bins
  // are taken. Slower than all-at-once, but a phone survives it.
  const limit = Math.max(1, Math.floor(deps.concurrency ?? DEFAULT_CONCURRENCY));
  let next = 0;
  const worker = async (): Promise<void> => {
    while (next < todo.length) {
      const t = todo[next++];
      try {
        const buf = await deps.decodeAudio(t.url);
        const ch0 = buf.getChannelData(0);
        // downsamplePeaks returns a Float32Array of abs-max per bin (0..1 if
        // input is 0..1). Convert to number[] for the PerTrackSummaryPeaks shape.
        tracks[t.id] = Array.from(downsamplePeaks(ch0, deps.binCount));
      } catch {
        tracks[t.id] = []; // leave empty → row renders .is-empty, no throw
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, todo.length) }, worker));

  return {
    v: 1,
    durationSec: existing?.durationSec ?? manifest.duration,
    binCount: deps.binCount,
    tracks,
  };
}
